import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ImageIcon, Eye, Edit, Trash2, Upload, Plus, X } from 'lucide-react';

const PartnerLogosTab = ({ authAxios }) => {
  const [partnerLogos, setPartnerLogos] = useState([]);
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [newLogoName, setNewLogoName] = useState('');
  const [newLogoLink, setNewLogoLink] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    fetchPartnerLogos();
  }, [authAxios]);

  const fetchPartnerLogos = async () => {
    try {
      const response = await authAxios.get('/admin/partner-logos');
      setPartnerLogos(response.data);
    } catch (error) {
      console.error('Failed to fetch partner logos:', error);
    }
  };

  const handleLogoImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image size must be less than 2MB'); return; }
    
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const response = await authAxios.post('/admin/partner-logos', {
          image_base64: base64, name: newLogoName || file.name, url: newLogoLink
        });
        setPartnerLogos([...partnerLogos, response.data.logo]);
        setNewLogoName('');
        setNewLogoLink('');
        toast.success('Partner logo added successfully!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleAddLogoByUrl = async () => {
    if (!newLogoUrl) { toast.error('Please enter an image URL'); return; }
    try {
      const response = await authAxios.post('/admin/partner-logos', {
        image_url: newLogoUrl, name: newLogoName, url: newLogoLink
      });
      setPartnerLogos([...partnerLogos, response.data.logo]);
      setNewLogoUrl('');
      setNewLogoName('');
      setNewLogoLink('');
      toast.success('Partner logo added successfully!');
    } catch (error) {
      toast.error('Failed to add logo');
    }
  };

  const handleDeleteLogo = async (logoId) => {
    try {
      await authAxios.delete(`/admin/partner-logos/${logoId}`);
      setPartnerLogos(partnerLogos.filter(l => l.id !== logoId));
      toast.success('Logo deleted');
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const handleToggleLogoVisibility = async (logoId) => {
    try {
      const response = await authAxios.put(`/admin/partner-logos/${logoId}/visibility`);
      setPartnerLogos(partnerLogos.map(l => l.id === logoId ? { ...l, visible: response.data.visible } : l));
      toast.success(response.data.visible ? 'Logo is now visible' : 'Logo is now hidden');
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleSaveLogoEdit = async () => {
    if (!editingLogo) return;
    try {
      const response = await authAxios.put(`/admin/partner-logos/${editingLogo.id}`, {
        name: editingLogo.name, url: editingLogo.url, image_url: editingLogo.image_url
      });
      setPartnerLogos(partnerLogos.map(l => l.id === editingLogo.id ? response.data.logo : l));
      setEditingLogo(null);
      toast.success('Logo updated successfully!');
    } catch (error) {
      toast.error('Failed to update logo');
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="partner-logos-tab">
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
          Partner Logos
        </h2>
        <p className="text-white/60 text-sm mb-6">Add partner or trusted company logos that will appear in a subtle strip above the footer.</p>
        
        {partnerLogos.length > 0 && (
          <div className="mb-8">
            <h3 className="text-white/50 text-xs font-mono uppercase mb-4">Current Logos ({partnerLogos.length})</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {partnerLogos.map((logo) => (
                <div key={logo.id} className={`bg-[#0A0A0A] border p-4 relative group transition-all ${logo.visible === false ? 'border-white/5 opacity-50' : 'border-white/10'}`}>
                  <img src={logo.image_url} alt={logo.name || 'Partner logo'} className={`h-12 w-auto mx-auto object-contain ${logo.visible === false ? 'grayscale' : ''}`} />
                  {logo.name && <p className="text-white/50 text-xs text-center mt-2 truncate">{logo.name}</p>}
                  {logo.visible === false && <span className="absolute top-2 left-2 text-[10px] font-mono text-white/40 bg-white/10 px-1.5 py-0.5">HIDDEN</span>}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => setEditingLogo({ ...logo })} className="p-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" data-testid={`edit-logo-${logo.id}`}><Edit className="w-4 h-4" /></button>
                    <button type="button" onClick={() => handleToggleLogoVisibility(logo.id)} className={`p-1 transition-colors ${logo.visible === false ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`} data-testid={`toggle-logo-${logo.id}`}><Eye className="w-4 h-4" /></button>
                    <button type="button" onClick={() => handleDeleteLogo(logo.id)} className="p-1 bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`delete-logo-${logo.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Logo Modal */}
        {editingLogo && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="edit-logo-modal">
            <div className="bg-[#111111] border border-white/10 p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-mono font-bold">Edit Logo</h3>
                <button type="button" onClick={() => setEditingLogo(null)} className="p-1 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div className="flex justify-center mb-4">
                  <img src={editingLogo.image_url} alt={editingLogo.name || 'Logo preview'} className="h-16 w-auto object-contain bg-white/5 p-2" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Logo Name</label>
                  <input type="text" value={editingLogo.name || ''} onChange={(e) => setEditingLogo({...editingLogo, name: e.target.value})} placeholder="e.g., Partner Company" className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Website URL</label>
                  <input type="url" value={editingLogo.url || ''} onChange={(e) => setEditingLogo({...editingLogo, url: e.target.value})} placeholder="https://example.com" className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Image URL</label>
                  <input type="url" value={editingLogo.image_url || ''} onChange={(e) => setEditingLogo({...editingLogo, image_url: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setEditingLogo(null)} className="flex-1 px-4 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5">Cancel</button>
                  <button type="button" onClick={handleSaveLogoEdit} className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90" data-testid="save-edit-logo">Save Changes</button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Add New Logo */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-white/50 text-xs font-mono uppercase mb-4">Add New Logo</h3>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Logo Name (Optional)</label>
              <input type="text" value={newLogoName} onChange={(e) => setNewLogoName(e.target.value)} placeholder="e.g., Partner Company Ltd" className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Website Link (Optional)</label>
              <input type="url" value={newLogoLink} onChange={(e) => setNewLogoLink(e.target.value)} placeholder="https://partner-website.com" className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Upload Image</label>
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoImageUpload} className="hidden" id="logo-upload" />
                <label htmlFor="logo-upload" className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-dashed border-white/20 text-white/50 font-mono text-sm hover:border-[#D4AF37]/50 cursor-pointer">
                  <Upload className="w-4 h-4" />{uploadingLogo ? 'Uploading...' : 'Choose Image'}
                </label>
              </div>
              <div className="flex items-end"><span className="text-white/30 text-sm px-4 pb-3">or</span></div>
              <div className="flex-1">
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Image URL</label>
                <div className="flex gap-2">
                  <input type="url" value={newLogoUrl} onChange={(e) => setNewLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" className="flex-1 bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                  <button onClick={handleAddLogoByUrl} disabled={!newLogoUrl} className="px-4 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 disabled:opacity-50"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogosTab;
